import { Formik } from 'formik';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { Navigate } from 'react-router-dom';
import { LOADING_TYPES, useCheckoutState } from '../../utils/checkoutState';
import { config } from '../../utils/config';
import { getInitialDynamicFieldValues } from '../../utils/dynamicFields';
import { FormNameProvider } from '../../utils/formName';
import { getInitialRoute } from '../../utils/routing';
import snakeCase from '../../utils/snakeCase';
import { pushErrorTrackingEventFromErrorObject } from '../../utils/tracking';
import AuthForm from '../molecules/AuthForm';
import { showDefaultError, showNotification } from '../molecules/Notifications';

const AuthStep = () => {
  const { checkoutState, signIn, register, setPasswordForCustomerNumber, isLoading, setLoading } = useCheckoutState();
  const isAuthBasedRedirectEnabled = useRef(true);
  const { formatMessage } = useIntl();
  const shouldShowSecondEmailField = config.features.secondEmailField.enabled;

  if (checkoutState.customer && isAuthBasedRedirectEnabled.current) {
    const basketLink = config.links.find(({ key }) => key === 'basket')?.link;
    if (basketLink) {
      window.location.href = basketLink;
      return null;
    } else {
      return <Navigate to={getInitialRoute(checkoutState)} replace />;
    }
  }

  if (checkoutState.customer) {
    return <Navigate to={getInitialRoute(checkoutState)} />;
  }

  const returningCustomer = window.ayCheckout?.customer;

  const initialValues = {
    type: 'signIn',
    gender: 'f',
    firstName: '',
    lastName: '',
    email: returningCustomer?.email ?? '',
    ...(shouldShowSecondEmailField && { secondEmailField: '' }),
    password: '',
    publicKey: '',
    emailHint: '',
    customerNumberEmail: '',
    signInWithCustomerNumber: 'inactive',
    ...getInitialDynamicFieldValues(checkoutState, 'register', 'guest')
  };

  const onSubmit = async ({
    type,
    gender,
    firstName,
    lastName,
    email,
    password,
    publicKey,
    birthDate,
    phoneNumber,
    isNewsletterSignUpAccepted,
    signInWithCustomerNumber,
    customerNumberEmail
  }: typeof initialValues) => {
    isAuthBasedRedirectEnabled.current = false;
    try {
      if (type === 'signIn' && signInWithCustomerNumber === 'setPassword') {
        setLoading(LOADING_TYPES.authSetPasswordForCustomerNumber);
        const response = await setPasswordForCustomerNumber({ publicKey, birthDate, email, password });
        if (response.customer && !response.customer.data.gender) {
          showNotification({
            level: 'info',
            text: formatMessage(
              { id: 'auth.notifications.preFilledGender' },
              { gender: formatMessage({ id: 'generic.salutation.options.m' }) }
            ),
            autoClose: true
          });
        }
      } else if (type === 'signIn' && signInWithCustomerNumber === 'verified') {
        setLoading(LOADING_TYPES.authLogin);
        await signIn({ email: customerNumberEmail, password });
      } else if (type === 'signIn' && signInWithCustomerNumber !== 'setPassword') {
        setLoading(LOADING_TYPES.authLogin);
        await signIn({ email, password });
      } else if (type === 'register') {
        setLoading(LOADING_TYPES.authRegister);
        await register({
          gender,
          firstName,
          lastName,
          email,
          password,
          hasSubscribedNewsletter: isNewsletterSignUpAccepted || false,
          source: '',
          ...(birthDate && { birthDate }),
          ...(phoneNumber && { phone: phoneNumber })
        });
      } else if (type === 'guest') {
        setLoading(LOADING_TYPES.authGuest);
        await register({
          gender,
          firstName,
          lastName,
          email,
          hasSubscribedNewsletter: isNewsletterSignUpAccepted || false,
          source: '',
          ...(birthDate && { birthDate }),
          ...(phoneNumber && { phone: phoneNumber })
        });
      }
    } catch (e) {
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, snakeCase(type));
    }
    setLoading(LOADING_TYPES.off);
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ values }) => (
        <FormNameProvider formName={values.type} isLoading={isLoading} setLoading={setLoading}>
          <AuthForm returningCustomer={returningCustomer} />
        </FormNameProvider>
      )}
    </Formik>
  );
};

export default AuthStep;
